import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { PageContext } from "~/App";
import { getCurrentPage } from "~/functions";
import PageHeader from "~/components/PageHeader";
import Breadcrumbs from "~/components/Breadcrumbs";
import FiltersBar from "~/components/FiltersBar";
import PeriodFilter from "../../components/filters/periodFilter";
import PeriodByFilter from "../../components/filters/periodByFilter";
import ChartReceivables from "../../components/charts/chartReceivables";
import GridReceivables from "../../components/grids/gridReceivables";
import api from "~/services/api";
import useReportsStore from "~/store/reportsSectionStore";
import { format, getMonth, getYear } from "date-fns";
import { ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "react-toastify";

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizePeriods = (node = {}, reportPeriods = []) => {
  if (Array.isArray(node.periods) && node.periods.length > 0) {
    return node.periods.map((period, index) => ({
      ...(reportPeriods[index] || {}),
      ...period,
      total: toNumber(period?.total),
    }));
  }

  const values = Array.isArray(node.values) ? node.values : [];
  return reportPeriods.map((period, index) => ({
    ...period,
    total: toNumber(values[index]),
  }));
};

const normalizeAccountNode = (node = {}, reportPeriods = []) => {
  const childrenSource =
    (Array.isArray(node.children) && node.children.length > 0 && node.children) ||
    (Array.isArray(node.accounts) && node.accounts.length > 0 && node.accounts) ||
    [];

  const periods = normalizePeriods(node, reportPeriods);
  const computedTotal = periods.reduce(
    (acc, period) => acc + toNumber(period.total),
    0
  );

  return {
    ...node,
    name: node.name || node.label || "",
    periods,
    total: toNumber(node.total) || computedTotal,
    children: childrenSource.map((child) =>
      normalizeAccountNode(child, reportPeriods)
    ),
  };
};

const normalizeReceivablesReport = (rawData) => {
  if (!rawData) return rawData;
  const reportPeriods = Array.isArray(rawData.periods) ? rawData.periods : [];
  const byChartOfAccount = Array.isArray(rawData.byChartOfAccount)
    ? rawData.byChartOfAccount.map((node) =>
        normalizeAccountNode(node, reportPeriods)
      )
    : [];

  return {
    ...rawData,
    byChartOfAccount,
  };
};

export function ReportFinancialInbounds() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { pages } = useContext(PageContext);
  const paths = pathname.split("/");
  const redirectRouteName = pages
    .find((page) => page.name === paths[1])
    .children.find((page) => page.path === `/${paths[1]}/${paths[2]}`)
    .children[0].path;
  useEffect(() => {
    if (pathname.toUpperCase() === `/${paths[1]}/${paths[2]}`.toUpperCase()) {
      navigate(`${redirectRouteName}`);
    }
  }, [pathname]);
  return <Outlet />;
}

export default function ReportFinancialReceivables() {
  const { filial } = useSelector((state) => state.auth);
  const currentPage = getCurrentPage();
  const { filters, setFilters, chartOfAccountSelected } = useReportsStore();
  const [loading, setLoading] = useState(true);

  const [data, setData] = useState(null);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState("");
  const [pmOpen, setPmOpen] = useState(false);
  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Partial Paid", label: "Partial Paid" },
    { value: "Pending", label: "Pending" },
    { value: "Renegociated", label: "Renegociated" },
  ];
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [statusOpen, setStatusOpen] = useState(false);
  const selectedStatusesCsv = selectedStatuses.join(",");
  const statusLabel =
    selectedStatuses.length === 0
      ? "All Statuses"
      : selectedStatuses.length === 1
      ? statusOptions.find((o) => o.value === selectedStatuses[0])?.label
      : `${selectedStatuses.length} selected`;

  function toggleStatus(value) {
    setSelectedStatuses((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  }

  function loadData() {
    setLoading(true);
    const reportToFilter = {
      Received: "received",
      Outstanding: "outstanding",
      Renegotiated: "renegociated",
      "Vacation Free": "vacation_free",
    };

    if (filters.report === "Custom") {
      const from = format(filters.period.from, "yyyy-MM-dd");
      const to = format(filters.period.to, "yyyy-MM-dd");

      const pmParam = selectedPaymentMethodId ? selectedPaymentMethodId : "all";
      const statusParam = selectedStatusesCsv || "all";

      const query = `/reports/receivables?period_from=${from}&period_to=${to}&period_by=${filters.period_by.value}&payment_method_ids=${pmParam}&statuses=${encodeURIComponent(statusParam)}`;

      api
        .get(query)
        .then(({ data }) => {
          setData(normalizeReceivablesReport(data));
          setLoading(false);
        })
        .catch((err) => {
          toast(err.response?.data?.error || "Failed to load data", { type: "error", autoClose: 3000 });
          setLoading(false);
        });
      return;
    }

    const filterParam = reportToFilter[filters.report]
      ? `&filter=${reportToFilter[filters.report]}`
      : "";
    const query = `/reports/receivables?period_from=${format(
      filters.period.from,
      "yyyy-MM-dd"
    )}&period_to=${format(filters.period.to, "yyyy-MM-dd")}&period_by=${
      filters.period_by.value
    }${filterParam}`;

    api
      .get(query)
      .then(({ data }) => {
        setData(normalizeReceivablesReport(data));
        setLoading(false);
      })
      .catch((err) => {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
        setLoading(false);
      });
  }

  useEffect(() => {
  
    const loadPaymentMethods = async () => {
      try {
        const { data } = await api.get("/paymentmethods/options");
      
        setPaymentMethodOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        setPaymentMethodOptions([]);
        
      }
    };
    loadPaymentMethods();
  }, []);

  useEffect(() => {
    if (chartOfAccountSelected) {
      setTimeout(() => {
        document.getElementById("reportPage").scrollTo(0, 500);
      }, 300);
    }
  }, [chartOfAccountSelected]);

  useEffect(() => {
    loadData();
  }, [filters, filial?.id]);

  
  useEffect(() => {
    if (filters.report === "Custom") {
      loadData();
    }
    
  }, [selectedPaymentMethodId, selectedStatusesCsv]);

  return (
    <div
      id="reportPage"
      className="scroll-smooth h-full bg-white flex flex-1 flex-col justify-start items-start rounded-tr-2xl px-4 overflow-y-scroll"
    >
      <PageHeader>
        <Breadcrumbs currentPage={currentPage} />
        <FiltersBar></FiltersBar>
      </PageHeader>

      <div className="w-full flex flex-row justify-start items-start gap-4">
        <div className="flex flex-col h-full px-2 min-w-44 w-44 items-center justify-start text-xs gap-4 bg-gray-100">
          <div className="text-sm p-2 py-4">
            <strong>Receivable Reports</strong>
          </div>
          {["Received", "Outstanding", "Renegotiated", "Vacation Free", "Custom"].map(
            (report, index) => (
              <button
                key={index}
                onClick={() => setFilters({ ...filters, report })}
                className={`text-sm w-full p-2 ${
                  filters.report === report && "bg-zinc-200"
                } rounded hover:bg-zinc-300`}
              >
                {report}
              </button>
            )
          )}
        </div>
        <div className="flex flex-col w-full max-w-272 flex-1 items-center justify-center text-xs">
          <div className="flex w-full flex-row justify-start items-center rounded-tr-2xl p-2 gap-4">
            {!loading && (
              <>
                {filters.report === "Custom" ? (
                  <>
                    <PeriodFilter customOnly />
                    <PeriodByFilter />
                  </>
                ) : (
                  <>
                    <PeriodFilter />
                    <PeriodByFilter />
                  </>
                )}
                {filters.report === "Custom" && (
                  <>
                    {/* Payment Methods dropdown */}
                    {pmOpen && (
                      <div onClick={() => setPmOpen(false)} className="fixed top-0 left-0 w-full h-full bg-transparent z-10"></div>
                    )}
                    <div className="flex items-center h-16">
                      <div className={`relative z-20 flex flex-col gap-y-2 text-xs bg-zinc-100 rounded p-1 min-h-12 min-w-48 border ${pmOpen ? "border-primary shadow-lg" : "border-transparent"}`}>
                        <button type="button" onClick={() => setPmOpen(!pmOpen)} className="flex flex-row justify-between items-center gap-x-2 text-xs cursor-pointer p-1 text-left">
                          <div className="flex flex-col">
                            <span>Payment Methods</span>
                            <strong className="text-sm">{(paymentMethodOptions.find((o) => o.id === selectedPaymentMethodId)?.description) || "All Methods"}</strong>
                          </div>
                          {pmOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        {pmOpen && (
                          <div className="absolute left-0 top-full mt-1 w-full bg-white border border-zinc-300 rounded shadow-lg max-h-64 overflow-auto">
                            <button type="button" className="w-full text-left p-2 hover:bg-zinc-100" onClick={() => { setSelectedPaymentMethodId(""); setPmOpen(false); }}>All Methods</button>
                            {paymentMethodOptions.map((pm) => (
                              <button key={pm.id} type="button" className="w-full text-left p-2 hover:bg-zinc-100" onClick={() => { setSelectedPaymentMethodId(pm.id); setPmOpen(false); }}>{pm.description}</button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Status dropdown */}
                    {statusOpen && (
                      <div onClick={() => setStatusOpen(false)} className="fixed top-0 left-0 w-full h-full bg-transparent z-10"></div>
                    )}
                    <div className="flex items-center h-16">
                      <div className={`relative z-20 flex flex-col gap-y-2 text-xs bg-zinc-100 rounded p-1 min-h-12 min-w-44 border ${statusOpen ? "border-primary shadow-lg" : "border-transparent"}`}>
                        <button type="button" onClick={() => setStatusOpen(!statusOpen)} className="flex flex-row justify-between items-center gap-x-2 text-xs cursor-pointer p-1 text-left">
                          <div className="flex flex-col">
                            <span>Status</span>
                            <strong className="text-sm">{statusLabel}</strong>
                          </div>
                          {statusOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                        {statusOpen && (
                          <div className="absolute left-0 top-full mt-1 w-full bg-white border border-zinc-300 rounded shadow-lg max-h-64 overflow-auto">
                            <button type="button" className="w-full text-left p-2 hover:bg-zinc-100 border-b border-zinc-200" onClick={() => setSelectedStatuses([])}>All Statuses</button>
                            {statusOptions.map((opt) => (
                              <label key={opt.value} className="flex flex-row items-center gap-2 w-full text-left p-2 hover:bg-zinc-100 cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="cursor-pointer"
                                  checked={selectedStatuses.includes(opt.value)}
                                  onChange={() => toggleStatus(opt.value)}
                                />
                                <span>{opt.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="flex w-full flex-1 flex-col justify-start items-start">
            <ChartReceivables data={data} />
            <GridReceivables
              data={data}
              setData={setData}
              selectedPaymentMethodId={selectedPaymentMethodId}
              selectedStatus={selectedStatusesCsv}
            />
          </div>

          {loading && (
            <div className="flex justify-center items-center h-screen absolute top-0 left-0 w-full bg-gray-500 bg-opacity-50 z-10">
              <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-700" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
